import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bitcoin,
  CheckCircle2,
  Clock3,
  Landmark,
  Percent,
  PlusCircle,
  RefreshCw,
  TrendingUp,
  Wallet,
} from 'lucide-react';
import { datadogRum } from '@datadog/browser-rum';
import { useAuth } from '../hooks/useAuth';
import accountService from '../services/accountService';
import authService from '../services/authService';
import investmentService from '../services/investmentService';
import Alert from '../components/common/Alert';
import Button from '../components/common/Button';
import Card from '../components/common/Card';
import { formatCurrency } from '../utils/formatters';
import { useT } from '../i18n';

const formatPercent = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '--';
  return `${(numeric * 100).toFixed(2)}%`;
};

const formatDateTime = (value) => {
  if (!value) return '--';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '--';
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const numeric = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const defaultAmountFor = (productCode) => (productCode === 'BTC' ? 50000 : 1000000);

// Rótulo singular/plural de posições, traduzido conforme o idioma ativo.
const positionLabel = (count, t) => (count === 1 ? t('investments.position_singular') : t('investments.position_plural'));

const productIcon = (code) => {
  if (code === 'BTC') return Bitcoin;
  return Percent;
};

const yieldFor = (position) => numeric(position.currentValue) - numeric(position.principalAmount);

const StatCard = ({ icon: Icon, label, value, helper, tone = 'purple' }) => {
  const colors = {
    purple: 'bg-violet-50 text-violet-700',
    green: 'bg-emerald-50 text-emerald-700',
    blue: 'bg-sky-50 text-sky-700',
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200/70 p-5 shadow-sm">
      <div className="flex items-center gap-3 mb-4">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${colors[tone]}`}>
          <Icon className="w-5 h-5" />
        </div>
        <p className="text-sm font-semibold text-slate-500">{label}</p>
      </div>
      <p className="text-2xl font-bold text-slate-900">{value}</p>
      {helper && <p className="text-xs text-slate-500 mt-1">{helper}</p>}
    </div>
  );
};

const ProductCard = ({ product, amount, disabled, onAmountChange, onSubscribe }) => {
  const { t } = useT();
  const Icon = productIcon(product.code);
  const isBitcoin = product.code === 'BTC';

  return (
    <Card className="h-full" bodyClassName="h-full flex flex-col">
      <div className="flex items-start gap-4">
        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${
          isBitcoin ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700'
        }`}>
          <Icon className="w-6 h-6" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <h3 className="text-lg font-bold text-slate-900">{product.name}</h3>
            <span className="px-2.5 py-1 rounded-full bg-slate-100 text-xs font-semibold text-slate-600">
              {product.riskLevel}
            </span>
          </div>
          <p className="text-sm text-slate-500 leading-relaxed">{product.description}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 my-5">
        <div className="rounded-xl bg-slate-50 p-3">
          <p className="text-xs text-slate-500 mb-1">{isBitcoin ? t('investments.current_quote') : t('investments.annual_rate')}</p>
          <p className="text-sm font-bold text-slate-900">
            {isBitcoin ? formatCurrency(numeric(product.referencePrice)) : formatPercent(product.annualRate)}
          </p>
        </div>
        <div className="rounded-xl bg-slate-50 p-3">
          <p className="text-xs text-slate-500 mb-1">{t('investments.category')}</p>
          <p className="text-sm font-bold text-slate-900 truncate">
            {isBitcoin ? t('investments.digital_asset') : t('investments.fixed_income')}
          </p>
        </div>
      </div>

      <div className="mt-auto space-y-3">
        <label className="block">
          <span className="text-xs font-semibold text-slate-500">{t('investments.contribution_amount')}</span>
          <input
            type="number"
            min="1"
            step="1000"
            value={amount}
            onChange={(event) => onAmountChange(product.code, event.target.value)}
            className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-900 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-100"
          />
        </label>
        <Button
          fullWidth
          size="sm"
          disabled={disabled}
          loading={disabled}
          icon={<PlusCircle className="w-4 h-4" />}
          onClick={() => onSubscribe(product)}
        >
          {isBitcoin ? t('investments.invest_bitcoin') : t('investments.invest_cdi')}
        </Button>
      </div>
    </Card>
  );
};

const MetricRow = ({ label, value, valueClassName = 'text-slate-900' }) => (
  <div className="flex items-center justify-between gap-3 py-2 border-b border-slate-100 last:border-0">
    <span className="text-sm text-slate-500">{label}</span>
    <span className={`text-sm font-bold text-right ${valueClassName}`}>{value}</span>
  </div>
);

const PositionCard = ({ position, syncing, onSync }) => {
  const { t } = useT();
  const accumulatedYield = yieldFor(position);
  const protocol = position.registryProtocol || position.auditCorrelationId || '--';

  return (
    <Card bodyClassName="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <h3 className="text-lg font-bold text-slate-900">{position.productName}</h3>
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-50 text-emerald-700">
              <CheckCircle2 className="w-3.5 h-3.5" />
              {t('investments.active')}
            </span>
          </div>
          <p className="text-xs text-slate-500">{t('investments.contract', { n: position.positionId })}</p>
        </div>
        <Button
          size="sm"
          variant="secondary"
          loading={syncing}
          disabled={syncing}
          icon={<RefreshCw className="w-4 h-4" />}
          onClick={() => onSync(position)}
        >
          {t('investments.update_value')}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
        <div>
          <MetricRow label={t('investments.applied_value')} value={formatCurrency(numeric(position.principalAmount))} />
          <MetricRow label={t('investments.current_balance')} value={formatCurrency(numeric(position.currentValue))} />
          <MetricRow
            label={t('investments.accumulated_yield')}
            value={formatCurrency(accumulatedYield)}
            valueClassName={accumulatedYield >= 0 ? 'text-emerald-700' : 'text-slate-900'}
          />
        </div>
        <div>
          <MetricRow label={t('investments.last_update')} value={formatDateTime(position.lastSyncedAt)} />
          <MetricRow label={t('investments.contracted_by')} value={position.contractedBy || '--'} />
          <MetricRow label={t('investments.protocol')} value={protocol} />
        </div>
      </div>
    </Card>
  );
};

const PasswordModal = ({
  product,
  amount,
  password,
  error,
  loading,
  onClose,
  onPasswordChange,
  onConfirm,
}) => {
  const { t } = useT();
  if (!product) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-slate-900/45 backdrop-blur-sm" onClick={loading ? undefined : onClose} />
      <div className="relative w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl shadow-slate-900/20">
        <div className="mb-5">
          <p className="text-sm font-semibold text-violet-700 mb-2">{t('investments.security_confirmation')}</p>
          <h2 className="text-xl font-bold text-slate-900">{t('investments.confirm_investment')}</h2>
          <p className="text-sm text-slate-500 mt-1">
            {t('investments.password_prompt', { amount: formatCurrency(Number(amount || 0)), product: product.name })}
          </p>
        </div>

        <form onSubmit={onConfirm} className="space-y-4">
          <label className="block">
            <span className="text-xs font-semibold text-slate-500">{t('investments.account_password')}</span>
            <input
              autoFocus
              type="password"
              inputMode="numeric"
              autoComplete="current-password"
              value={password}
              onChange={(event) => onPasswordChange(event.target.value)}
              className="mt-1 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-lg font-bold tracking-normal text-slate-900 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-100"
              placeholder={t('investments.enter_password')}
              disabled={loading}
            />
          </label>

          {error && (
            <div className="rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
              {error}
            </div>
          )}

          <div className="flex flex-col-reverse sm:flex-row gap-3 pt-2">
            <Button
              type="button"
              variant="secondary"
              fullWidth
              disabled={loading}
              onClick={onClose}
            >
              {t('investments.cancel')}
            </Button>
            <Button
              type="submit"
              fullWidth
              loading={loading}
              disabled={loading}
            >
              {t('investments.confirm_investment')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

const InvestmentsPage = () => {
  const { user, loading: authLoading } = useAuth();
  const { t } = useT();
  const navigate = useNavigate();
  const [accountData, setAccountData] = useState(null);
  const [products, setProducts] = useState([]);
  const [positions, setPositions] = useState([]);
  const [amounts, setAmounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [subscribingCode, setSubscribingCode] = useState('');
  const [syncingId, setSyncingId] = useState('');
  const [pendingProduct, setPendingProduct] = useState(null);
  const [investmentPassword, setInvestmentPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [validatingPassword, setValidatingPassword] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login', { replace: true });
    }
  }, [authLoading, user, navigate]);

  const resolveAccount = useCallback(async () => {
    const storedAccountId = user?.accountId || localStorage.getItem('accountId');
    const cpf = user?.cpf || localStorage.getItem('cpf');

    if (storedAccountId) {
      return {
        id: Number(storedAccountId),
        cpf,
        nome: user?.nome || localStorage.getItem('nome') || 'Cliente DogBank',
      };
    }

    if (!cpf) {
      throw new Error(t('investments.account_not_found'));
    }

    const account = await accountService.getAccountInfo(cpf);
    return {
      ...account,
      id: account.id || account.accountId,
      cpf,
      nome: user?.nome || account.userName || localStorage.getItem('nome') || 'Cliente DogBank',
    };
  }, [user]);

  const loadInvestments = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const account = await resolveAccount();
      const [fetchedProducts, fetchedPositions] = await Promise.all([
        investmentService.getProducts(),
        investmentService.getPositions(account.id),
      ]);

      if (!Array.isArray(fetchedProducts) || !Array.isArray(fetchedPositions)) {
        throw new Error(t('investments.backend_unavailable'));
      }

      const nextAmounts = {};
      fetchedProducts.forEach((product) => {
        nextAmounts[product.code] = amounts[product.code] || defaultAmountFor(product.code);
      });

      setAccountData(account);
      setProducts(fetchedProducts);
      setPositions(fetchedPositions);
      setAmounts(nextAmounts);

      datadogRum.addAction('dogbank.web.investments.loaded', {
        account_id: account.id,
        product_count: fetchedProducts.length,
        position_count: fetchedPositions.length,
        technical_divergence_count: fetchedPositions.filter((position) => position.status !== 'SYNCED').length,
      });
    } catch (err) {
      console.error('Error loading investments:', err);
      setError(err.response?.data?.message || err.message || t('investments.load_error'));
      datadogRum.addError(err, { flow: 'web_investments_load' });
    } finally {
      setLoading(false);
    }
  }, [amounts, resolveAccount]);

  useEffect(() => {
    if (user || localStorage.getItem('cpf')) {
      loadInvestments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const summary = useMemo(() => {
    const appliedTotal = positions.reduce((total, position) => total + numeric(position.principalAmount), 0);
    const currentTotal = positions.reduce((total, position) => total + numeric(position.currentValue), 0);
    const yieldTotal = currentTotal - appliedTotal;

    return { appliedTotal, currentTotal, yieldTotal };
  }, [positions]);

  const handleAmountChange = (productCode, value) => {
    setAmounts((current) => ({ ...current, [productCode]: value }));
  };

  const closePasswordModal = () => {
    if (validatingPassword || subscribingCode) return;
    setPendingProduct(null);
    setInvestmentPassword('');
    setPasswordError('');
  };

  const requestSubscription = (product) => {
    setError('');
    setNotice('');
    setPasswordError('');
    setInvestmentPassword('');
    setPendingProduct(product);
  };

  const upsertPosition = (updatedPosition) => {
    setPositions((current) => {
      const exists = current.some((position) => position.positionId === updatedPosition.positionId);
      if (!exists) return [updatedPosition, ...current];
      return current.map((position) => (
        position.positionId === updatedPosition.positionId ? updatedPosition : position
      ));
    });
  };

  const executeSubscription = async (product) => {
    const amount = Number(amounts[product.code] || defaultAmountFor(product.code));
    if (!Number.isFinite(amount) || amount <= 0) {
      setError(t('investments.invalid_amount'));
      return;
    }

    try {
      setError('');
      setNotice('');
      setSubscribingCode(product.code);
      datadogRum.addAction('dogbank.web.investments.subscription.started', {
        account_id: accountData?.id,
        product_code: product.code,
        amount,
      });

      const position = await investmentService.subscribe({
        accountId: accountData.id,
        cpf: accountData.cpf || user?.cpf || localStorage.getItem('cpf'),
        userName: user?.nome || accountData.nome || accountData.userName || 'Cliente DogBank',
        productCode: product.code,
        amount,
        requestedBy: 'frontend-react',
      });

      upsertPosition(position);
      setNotice(t('investments.subscription_success'));
      setPendingProduct(null);
      setInvestmentPassword('');
      setPasswordError('');

      datadogRum.addAction('dogbank.web.investments.subscription.completed', {
        account_id: accountData.id,
        product_code: product.code,
        position_id: position.positionId,
        backend_status: position.status,
        amount,
      });
    } catch (err) {
      console.error('Error subscribing to investment:', err);
      setError(err.response?.data?.message || err.message || t('investments.subscription_error'));
      datadogRum.addError(err, {
        flow: 'web_investment_subscription',
        product_code: product.code,
        amount,
      });
    } finally {
      setSubscribingCode('');
    }
  };

  const handlePasswordConfirm = async (event) => {
    event.preventDefault();

    if (!pendingProduct) return;

    const cpf = accountData?.cpf || user?.cpf || localStorage.getItem('cpf');
    if (!cpf) {
      setPasswordError(t('investments.session_error'));
      return;
    }

    if (!investmentPassword.trim()) {
      setPasswordError(t('investments.password_required'));
      return;
    }

    try {
      setValidatingPassword(true);
      setPasswordError('');

      datadogRum.addAction('dogbank.web.investments.password_validation.started', {
        account_id: accountData?.id,
        product_code: pendingProduct.code,
      });

      const result = await authService.validatePassword(cpf, investmentPassword);
      if (!result?.valid) {
        setPasswordError(result?.message || t('investments.wrong_password'));
        datadogRum.addAction('dogbank.web.investments.password_validation.failed', {
          account_id: accountData?.id,
          product_code: pendingProduct.code,
        });
        return;
      }

      datadogRum.addAction('dogbank.web.investments.password_validation.completed', {
        account_id: accountData?.id,
        product_code: pendingProduct.code,
      });

      await executeSubscription(pendingProduct);
    } catch (err) {
      console.error('Error validating investment password:', err);
      setPasswordError(err.response?.data?.message || t('investments.password_validation_error'));
      datadogRum.addError(err, {
        flow: 'web_investment_password_validation',
        product_code: pendingProduct.code,
      });
    } finally {
      setValidatingPassword(false);
    }
  };

  const handleSync = async (position) => {
    try {
      setError('');
      setNotice('');
      setSyncingId(position.positionId);
      datadogRum.addAction('dogbank.web.investments.refresh.started', {
        account_id: accountData?.id,
        position_id: position.positionId,
        product_code: position.productCode,
      });

      const updated = await investmentService.syncPosition(position.positionId);
      upsertPosition(updated);
      setNotice(t('investments.value_updated'));

      datadogRum.addAction('dogbank.web.investments.refresh.completed', {
        position_id: updated.positionId,
        product_code: updated.productCode,
        backend_status: updated.status,
      });
    } catch (err) {
      console.error('Error updating investment:', err);
      setError(t('investments.sync_error'));
      datadogRum.addError(err, {
        flow: 'web_investment_refresh',
        position_id: position.positionId,
        product_code: position.productCode,
      });
    } finally {
      setSyncingId('');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-purple-200 rounded-full animate-spin border-t-purple-600 mx-auto mb-6" />
          <p className="text-slate-600 font-medium">{t('investments.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-slide-up">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{t('investments.title')}</h1>
          <p className="text-sm text-slate-500">
            {t('investments.subtitle')}
          </p>
        </div>
        <Button
          variant="secondary"
          size="sm"
          icon={<RefreshCw className="w-4 h-4" />}
          onClick={loadInvestments}
        >
          {t('investments.refresh')}
        </Button>
      </div>

      {error && <Alert type="error" message={error} onClose={() => setError('')} />}
      {notice && <Alert type="success" message={notice} onClose={() => setNotice('')} />}

      <section className="rounded-3xl bg-slate-900 text-white p-6 md:p-8 shadow-lg shadow-slate-900/10 overflow-hidden relative">
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <p className="text-sm font-semibold text-white/70 mb-2">{t('investments.portfolio')}</p>
            <h2 className="text-4xl md:text-5xl font-extrabold tracking-normal">
              {formatCurrency(summary.currentTotal)}
            </h2>
            <p className="text-sm text-white/70 mt-3">
              {t('investments.positions_on_account', { count: positions.length, label: positionLabel(positions.length, t), account: accountData?.id || '--' })}
            </p>
          </div>
          <div className="w-16 h-16 rounded-2xl bg-white/10 flex items-center justify-center">
            <TrendingUp className="w-8 h-8 text-white" />
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard
          icon={Landmark}
          label={t('investments.total_applied')}
          value={formatCurrency(summary.appliedTotal)}
          helper={t('investments.sum_contributions')}
          tone="blue"
        />
        <StatCard
          icon={Wallet}
          label={t('investments.current_balance')}
          value={formatCurrency(summary.currentTotal)}
          helper={t('investments.wallet_accounted')}
          tone="purple"
        />
        <StatCard
          icon={Clock3}
          label={t('investments.yield')}
          value={formatCurrency(summary.yieldTotal)}
          helper={t('investments.accumulated_variation')}
          tone={summary.yieldTotal >= 0 ? 'green' : 'blue'}
        />
      </section>

      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-slate-900">{t('investments.available_products')}</h2>
        </div>
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
          {products.map((product) => (
            <ProductCard
              key={product.code}
              product={product}
              amount={amounts[product.code] || defaultAmountFor(product.code)}
              disabled={subscribingCode === product.code}
              onAmountChange={handleAmountChange}
              onSubscribe={requestSubscription}
            />
          ))}
        </div>
      </section>

      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-slate-900">{t('investments.my_wallet')}</h2>
          <span className="text-xs font-semibold text-slate-500 bg-white px-3 py-1.5 rounded-full border border-slate-200">
            {positions.length} {positionLabel(positions.length, t)}
          </span>
        </div>

        {positions.length === 0 ? (
          <Card className="text-center" bodyClassName="py-12">
            <div className="w-12 h-12 rounded-2xl bg-violet-50 text-violet-700 flex items-center justify-center mx-auto mb-4">
              <TrendingUp className="w-6 h-6" />
            </div>
            <h3 className="font-bold text-slate-900 mb-1">{t('investments.no_investments')}</h3>
            <p className="text-sm text-slate-500">{t('investments.wallet_empty_hint')}</p>
          </Card>
        ) : (
          <div className="space-y-4">
            {positions.map((position) => (
              <PositionCard
                key={position.positionId}
                position={position}
                syncing={syncingId === position.positionId}
                onSync={handleSync}
              />
            ))}
          </div>
        )}
      </section>

      <PasswordModal
        product={pendingProduct}
        amount={pendingProduct ? amounts[pendingProduct.code] || defaultAmountFor(pendingProduct.code) : 0}
        password={investmentPassword}
        error={passwordError}
        loading={validatingPassword || Boolean(subscribingCode)}
        onClose={closePasswordModal}
        onPasswordChange={setInvestmentPassword}
        onConfirm={handlePasswordConfirm}
      />
    </div>
  );
};

export default InvestmentsPage;
